import Matter from "matter-js";
import type { GameConfig } from "../config/GameConfig";
import type { WorldBounds } from "../core/WorldBounds";
import type { ForceField } from "../physics/ForceField";
import type { PhysicsWorld } from "../physics/PhysicsWorld";
import { Bubble, createBubbleBody } from "./Bubble";
import type { Divider } from "./Divider";
import { bezierEaseInOut, clamp, oppositeError } from "../core/math";

export class BubbleManager {
  readonly bubbles: Bubble[] = [];
  onMeeting: ((points: number) => void) | null = null;
  onAbsorb: ((radius: number, above: boolean) => void) | null = null;
  onMerge: ((radius: number, above: boolean, otherRadius: number) => void) | null = null;
  onSpawn: ((above: boolean, x: number) => void) | null = null;
  private readonly byBodyId = new Map<number, Bubble>();
  private readonly growingByPointer = new Map<number, Bubble>();

  constructor(
    private readonly config: GameConfig,
    private readonly physics: PhysicsWorld,
    private readonly bounds: WorldBounds,
  ) {}

  get(body: Matter.Body): Bubble | undefined {
    return this.byBodyId.get(body.id);
  }

  startGrow(x: number, y: number, pointerId: number, divider: Divider): Bubble | null {
    if (this.growingByPointer.has(pointerId)) return this.growingByPointer.get(pointerId)!;
    if (this.aliveCount() >= this.config.bubble.maxCount) return null;

    const radius = this.config.bubble.minRadius;
    const body = createBubbleBody(x, y, radius, this.config, false);
    const bubble = new Bubble(body, radius, pointerId, performance.now(), divider.isAbove(x, y));
    this.add(bubble);
    this.growingByPointer.set(pointerId, bubble);
    this.resolveGrowing(bubble, divider);
    this.onSpawn?.(bubble.bornAbove, bubble.x);
    return bubble;
  }

  moveGrow(pointerId: number, x: number, y: number, divider: Divider): void {
    const bubble = this.growingByPointer.get(pointerId);
    if (!bubble || bubble.state !== "Growing") return;

    if (!divider.allowsSpawn(x, y) || divider.isAbove(x, y) !== bubble.bornAbove) {
      this.release(pointerId);
      return;
    }

    bubble.follow(x, y);
    this.resolveGrowing(bubble, divider);
  }

  release(pointerId: number): void {
    const bubble = this.growingByPointer.get(pointerId);
    this.growingByPointer.delete(pointerId);
    if (!bubble || bubble.state !== "Growing") return;

    const x = bubble.x;
    const y = bubble.y;
    const radius = bubble.radius;
    this.physics.remove(bubble.body);
    this.byBodyId.delete(bubble.id);

    const live = createBubbleBody(x, y, radius, this.config, true);
    bubble.attachLiveBody(live);
    this.byBodyId.set(live.id, bubble);
    this.physics.add(live);
    Matter.Body.setVelocity(live, { x: 0, y: 0 });
    Matter.Body.setAngularVelocity(live, 0);
  }

  cancelGrow(pointerId: number): void {
    const bubble = this.growingByPointer.get(pointerId);
    this.growingByPointer.delete(pointerId);
    if (!bubble || bubble.state !== "Growing") return;
    this.destroy(bubble);
  }

  update(dt: number, time: number, divider: Divider, field: ForceField): void {
    const cfg = this.config.bubble;

    for (let i = this.bubbles.length - 1; i >= 0; i--) {
      const bubble = this.bubbles[i];
      if (bubble.state === "Absorbed") {
        this.destroy(bubble);
        continue;
      }

      if (bubble.state === "Growing") {
        const next = Math.min(cfg.maxRadius, bubble.radius + cfg.growRate * dt);
        bubble.setRadius(next);
        if (!divider.allowsSpawn(bubble.x, bubble.y) && bubble.pointerId !== null) {
          this.release(bubble.pointerId);
          continue;
        }
        this.resolveGrowing(bubble, divider);
        continue;
      }

      if (bubble.state === "Absorbing") continue;
      if (bubble.state === "Meeting") continue;

      const next =
        bubble.radius - (cfg.shrinkRate + bubble.radius * cfg.shrinkRateRel) * dt;
      if (next < cfg.minRadius) {
        this.destroy(bubble);
        continue;
      }
      bubble.setRadius(next);

      const above = divider.isAbove(bubble.x, bubble.y);
      bubble.body.frictionAir = above ? cfg.airFriction : cfg.waterFriction;
      bubble.body.restitution = cfg.restitution;

      const sample = field.sample(bubble.x, bubble.y, time, divider);
      const fieldMul = bubble.fieldInfluence(this.config);
      Matter.Body.applyForce(bubble.body, bubble.body.position, {
        x: sample.x * fieldMul,
        y: sample.y * fieldMul,
      });

      const attractMul = bubble.attractionInfluence(this.config);
      const k =
        this.config.divider.attractionK +
        (this.config.divider.attractionWhenSmall - this.config.divider.attractionK) *
          attractMul;
      if (divider.isOval) {
        const hit = divider.closest(bubble.x, bubble.y);
        Matter.Body.applyForce(bubble.body, bubble.body.position, {
          x: (hit.x - bubble.x) * k * attractMul * bubble.body.mass,
          y: (hit.y - bubble.y) * k * attractMul * bubble.body.mass,
        });
      } else {
        const targetY = divider.yAt(bubble.x);
        const dy = targetY - bubble.y;
        Matter.Body.applyForce(bubble.body, bubble.body.position, {
          x: 0,
          y: dy * k * attractMul * bubble.body.mass,
        });
      }
    }

    this.resolveMeetings(divider);

    for (let i = this.bubbles.length - 1; i >= 0; i--) {
      const bubble = this.bubbles[i];
      if (bubble.state !== "Meeting") continue;
      const mul = this.config.divider.absorbRateMul;
      const next = bubble.radius - mul * (cfg.shrinkRate + bubble.radius * cfg.shrinkRateRel) * dt;
      const progress = clamp(1 - next / bubble.meetStartRadius, 0, 1);
      const eased = bezierEaseInOut(
        progress,
        this.config.score.meetBezierIn,
        this.config.score.meetBezierOut,
      );
      bubble.meetGlow = eased;
      if (next <= 0.6) {
        this.destroy(bubble);
        continue;
      }
      bubble.setRadius(next);
      const hit = divider.closest(bubble.x, bubble.y);
      const side = bubble.bornAbove ? -1 : 1;
      const pull = 1 - eased * 0.88;
      bubble.follow(
        hit.x + hit.nx * side * bubble.radius * pull,
        hit.y + hit.ny * side * bubble.radius * pull,
      );
    }

    for (let i = this.bubbles.length - 1; i >= 0; i--) {
      const bubble = this.bubbles[i];
      if (bubble.state !== "Absorbing") continue;
      const mul = this.config.divider.absorbRateMul;
      const next = bubble.radius - mul * (cfg.shrinkRate + bubble.radius * cfg.shrinkRateRel) * dt;
      const done = divider.advanceAbsorb(bubble.id, Math.max(0, next));
      if (done || next <= 0.6) {
        const holdOpposite =
          this.config.score.meetMode === "opposite" &&
          performance.now() - bubble.absorbStartedAt < this.config.score.meetGraceMs;
        if (holdOpposite) {
          bubble.setRadius(Math.max(0.7, bubble.radius));
          const pose = divider.absorbPose(bubble.id, bubble.radius);
          if (pose) bubble.follow(pose.x, pose.y);
          continue;
        }
        divider.finishAbsorb(bubble.id);
        this.destroy(bubble);
        continue;
      }
      bubble.setRadius(next);
      const pose = divider.absorbPose(bubble.id, bubble.radius);
      if (pose) bubble.follow(pose.x, pose.y);
    }

    for (let i = this.bubbles.length - 1; i >= 0; i--) {
      const bubble = this.bubbles[i];
      if (bubble.state !== "Alive" && bubble.state !== "Growing") continue;
      if (!this.hitsDivider(bubble, divider)) continue;
      this.absorbIntoDivider(bubble, divider);
    }
  }

  clear(): void {
    for (let i = this.bubbles.length - 1; i >= 0; i--) {
      this.destroy(this.bubbles[i]);
    }
    this.growingByPointer.clear();
  }

  destroy(bubble: Bubble): void {
    if (bubble.pointerId !== null) {
      this.growingByPointer.delete(bubble.pointerId);
    }
    this.byBodyId.delete(bubble.id);
    const idx = this.bubbles.indexOf(bubble);
    if (idx >= 0) this.bubbles.splice(idx, 1);
    this.physics.remove(bubble.body);
  }

  mergeInto(keep: Bubble, eaten: Bubble): void {
    if (keep.state !== "Alive") return;
    if (eaten.state !== "Alive" && eaten.state !== "Growing") return;

    const newRadius = Math.sqrt((keep.area() + eaten.area()) / Math.PI);
    let x = keep.x;
    let y = keep.y;
    let vx = keep.body.velocity.x;
    let vy = keep.body.velocity.y;

    if (eaten.state === "Alive") {
      const m1 = this.finiteMass(keep);
      const m2 = this.finiteMass(eaten);
      const total = m1 + m2;
      x = (keep.x * m1 + eaten.x * m2) / total;
      y = (keep.y * m1 + eaten.y * m2) / total;
      vx = (keep.body.velocity.x * m1 + eaten.body.velocity.x * m2) / total;
      vy = (keep.body.velocity.y * m1 + eaten.body.velocity.y * m2) / total;
    }

    const eatenR = eaten.radius;
    this.destroy(eaten);
    keep.setRadius(Math.min(newRadius, this.maxAbsorbRadius()));
    this.onMerge?.(keep.radius, keep.bornAbove, eatenR);
    Matter.Body.setPosition(keep.body, { x, y });
    Matter.Body.setPosition(keep.body, { x, y });
    Matter.Body.setVelocity(keep.body, { x: vx, y: vy });
  }

  maxAbsorbRadius(): number {
    return this.config.bubble.maxRadius * this.config.merge.absorbScale;
  }

  canAbsorbMore(bubble: Bubble): boolean {
    return bubble.state === "Alive" && bubble.radius < this.maxAbsorbRadius() - 0.5;
  }

  private resolveGrowing(bubble: Bubble, divider: Divider): void {
    if (bubble.state !== "Growing") return;
    if (this.hitsDivider(bubble, divider)) return;
    const host = this.findAbsorbHost(bubble);
    if (host) this.absorbGrowingInto(host, bubble);
  }

  private hitsDivider(bubble: Bubble, divider: Divider): boolean {
    if (divider.isAbove(bubble.x, bubble.y) !== bubble.bornAbove) return true;
    return divider.distanceTo(bubble.x, bubble.y) - bubble.radius <= this.config.divider.absorbDistance;
  }

  private resolveMeetings(divider: Divider): void {
    const now = performance.now();
    const grace = this.config.score.meetGraceMs;
    const slack = this.config.score.meetSlack;
    const candidates = this.bubbles.filter((b) => {
      if (b.state === "Meeting") return false;
      if (b.state === "Alive" && this.hitsDivider(b, divider)) return true;
      if (b.state === "Growing" && this.hitsDivider(b, divider)) return true;
      if (b.state === "Absorbing" && now - b.absorbStartedAt <= grace) return true;
      return false;
    });

    if (this.config.score.meetMode === "opposite") {
      this.resolveOppositeMeetings(candidates, divider);
      return;
    }

    const used = new Set<number>();
    for (const a of candidates) {
      if (used.has(a.id)) continue;
      let partner: Bubble | null = null;
      let best = Infinity;
      for (const b of candidates) {
        if (b.id === a.id || used.has(b.id)) continue;
        if (a.bornAbove === b.bornAbove) continue;
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        const reach = a.radius + b.radius + slack;
        if (dist <= reach && dist < best) {
          partner = b;
          best = dist;
        }
      }
      if (!partner) continue;
      used.add(a.id);
      used.add(partner.id);
      this.completeMeeting(a, partner, divider);
    }
  }

  private resolveOppositeMeetings(candidates: Bubble[], divider: Divider): void {
    const maxErr = (this.config.score.oppositeErrorDeg * Math.PI) / 180;
    const used = new Set<number>();
    for (const a of candidates) {
      if (used.has(a.id)) continue;
      const angA = divider.visualAngle(a.x, a.y);
      let partner: Bubble | null = null;
      let best = Infinity;
      for (const b of candidates) {
        if (b.id === a.id || used.has(b.id)) continue;
        const err = oppositeError(angA, divider.visualAngle(b.x, b.y));
        if (err <= maxErr && err < best) {
          partner = b;
          best = err;
        }
      }
      if (!partner) continue;
      used.add(a.id);
      used.add(partner.id);
      this.completeMeeting(a, partner, divider);
    }
  }

  private completeMeeting(a: Bubble, b: Bubble, divider: Divider): void {
    divider.cancelAbsorb(a.id);
    divider.cancelAbsorb(b.id);
    const cfg = this.config.score;
    const points = Math.max(
      1,
      Math.round(cfg.meetBase + (a.area() + b.area()) * cfg.areaFactor),
    );
    this.onMeeting?.(points);
    if (a.pointerId !== null) this.growingByPointer.delete(a.pointerId);
    if (b.pointerId !== null) this.growingByPointer.delete(b.pointerId);
    a.beginMeeting();
    b.beginMeeting();
  }

  private absorbIntoDivider(bubble: Bubble, divider: Divider): void {
    if (bubble.state !== "Growing" && bubble.state !== "Alive") return;
    if (bubble.pointerId !== null) {
      this.growingByPointer.delete(bubble.pointerId);
    }
    divider.beginAbsorb(bubble.id, bubble.x, bubble.y, bubble.radius);
    this.onAbsorb?.(bubble.radius, bubble.bornAbove);
    bubble.absorb();
  }

  private findAbsorbHost(growing: Bubble): Bubble | null {
    let best: Bubble | null = null;
    let bestDist = Infinity;
    for (const other of this.bubbles) {
      if (other === growing || !this.canAbsorbMore(other)) continue;
      if (other.bornAbove !== growing.bornAbove) continue;
      const dist = Math.hypot(other.x - growing.x, other.y - growing.y);
      const reach = other.radius + growing.radius;
      if (dist <= reach && dist < bestDist) {
        best = other;
        bestDist = dist;
      }
    }
    return best;
  }

  private absorbGrowingInto(host: Bubble, growing: Bubble): void {
    this.mergeInto(host, growing);
  }

  private finiteMass(bubble: Bubble): number {
    const mass = bubble.body.mass;
    if (Number.isFinite(mass) && mass > 0 && !bubble.body.isStatic) return mass;
    return Math.max(1e-6, bubble.area() * this.config.bubble.density);
  }

  private add(bubble: Bubble): void {
    this.bubbles.push(bubble);
    this.byBodyId.set(bubble.id, bubble);
    this.physics.add(bubble.body);
  }

  private aliveCount(): number {
    return this.bubbles.length;
  }
}
